import { useEffect, useState } from 'react';

// Official store URLs
export const APP_STORE_URL = 'https://apps.apple.com/app/vitala-me/id6770978839';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=me.vitala.twa';

/**
 * Detect device platform from User-Agent.
 * Returns 'ios' | 'android' | 'desktop'.
 */
function detectPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/**
 * AppStoreBadges — renders official Apple App Store + Google Play badges.
 *
 * Props:
 *  - variant: "smart" (default) shows only the relevant store on mobile, both on desktop.
 *             "both" always shows both badges.
 *  - size:    "sm" | "md" (default) | "lg"
 *  - align:   "start" | "center" (default)
 *  - className: optional extra classes for the wrapper
 */
export function AppStoreBadges({ variant = 'smart', size = 'md', align = 'center', className = '' }) {
  const [platform, setPlatform] = useState('desktop');

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const showApple = variant === 'both' || platform === 'desktop' || platform === 'ios';
  const showGoogle = variant === 'both' || platform === 'desktop' || platform === 'android';

  const heightCls = size === 'sm' ? 'h-10' : size === 'lg' ? 'h-14' : 'h-12';
  const justifyCls = align === 'start' ? 'justify-start' : 'justify-center';

  return (
    <div
      className={`flex flex-wrap items-center gap-3 ${justifyCls} ${className}`}
      data-testid="app-store-badges"
    >
      {showApple && (
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download Vitala on the App Store"
          data-testid="app-store-badge"
          className="inline-block transition-transform hover:scale-[1.03] active:scale-[0.98]"
        >
          <img
            src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
            alt="Download on the App Store"
            className={`${heightCls} w-auto`}
            loading="lazy"
          />
        </a>
      )}
      {showGoogle && (
        <a
          href={PLAY_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get Vitala on Google Play"
          data-testid="play-store-badge"
          className="inline-block transition-transform hover:scale-[1.03] active:scale-[0.98]"
        >
          <img
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
            alt="Get it on Google Play"
            className={`${heightCls} w-auto`}
            loading="lazy"
          />
        </a>
      )}
    </div>
  );
}

export default AppStoreBadges;
